import * as R from "ramda";
import React from "react";

import {
  IClientPatch,
  IPatchAction,
  IPatchAddAction,
  IPatchDeleteAction,
  IPatchInsertAction,
  IPatchMoveAction,
  IPatchSubstAction,
  isPublicationPatch,
  PatchType,
} from "../../../course-author/helpers/patches";
import { findComponentPath, getPatchTarget } from "../../../course-author/helpers/patches/util";

import { courseComponentViews, fullCourseViews } from "../../../../utils/courseViews";
import { PreferredLocale } from "../../../../utils/locales";

import {
  DefaultNames,
  ICoursePublication,
  ICourseUnit,
  IFullCourse,
  isCourseLocalizedAnswer,
  isCourseLocalizedText,
  isInstruction,
  isLocalized,
  isUnit,
} from "../../../course-author/types";

const addPatchDescription = (change: IClientPatch<IPatchAddAction<any>>) => {
  const addAction = change.patch.action;
  const addedItem = addAction.item;
  const getTitle = () => {
    if (isLocalized(addedItem)) {
      const localeInfo = ` for locale '${addedItem.locale}'`;
      if (isCourseLocalizedText(addedItem)) {
        return `text '${addedItem.value.text}'${localeInfo}`;
      } else if (isCourseLocalizedAnswer(addedItem)) {
        return `answers '${addedItem.value.value}'${localeInfo}`;
      } else {
        return `an item${localeInfo}`;
      }
    } else {
      if (isPublicationPatch(change)) {
        const pubAddAction = change.patch.action as IPatchAddAction<ICoursePublication>;
        return `publication of version ${pubAddAction.item.revision}`;
      }
      return `an item`;
    }
  };
  return (
    <>
      <strong>Added</strong>: <span>{getTitle()}</span>
    </>
  );
};

const delPatchDescription = (change: IClientPatch<IPatchDeleteAction<any>>) => {
  const action = change.patch.action;
  const elem = action.elem;
  return (
    <>
      <strong>Deleted</strong>: <span>{`'${elem.toString()}' from '${change.patch.position}'`}</span>
    </>
  );
};

const substDeletePatchDescription = (course: IFullCourse, change: IClientPatch<IPatchSubstAction<any>>) => {
  const substAction = change.patch.action;
  const pos = change.patch.position.split(".");
  const act = substAction.y ? "Deleted" : "Undeleted";
  const getTitle = () => {
    if (isPublicationPatch(change)) {
      const pubs = fullCourseViews.publications(course) || [];
      const index = parseInt(R.nth(-2, pos) || "", 10);
      const pub = R.find(p => p.id === index, pubs) || ({} as ICoursePublication);
      return `publication of version ${pub.revision}`;
    }
    return "an item";
  };
  return (
    <>
      <strong>{act}</strong>: <span>{getTitle()}</span>
    </>
  );
};

const substPatchDescription = (course: IFullCourse, change: IClientPatch<IPatchSubstAction<any>>) => {
  const pos = change.patch.position.split(".");
  const substAction = change.patch.action;
  if (R.last(pos) === "deleted") {
    return substDeletePatchDescription(course, change);
  }
  return (
    <>
      <p>
        <strong>Change {R.last(pos)} From</strong>: <span>{`${substAction.x}`}</span>
      </p>
      <p>
        <strong>To</strong>: <span>{`${substAction.y}`}</span>
      </p>
    </>
  );
};

const insPatchDescription = (change: IClientPatch<IPatchInsertAction<any>>) => {
  const substAction = change.patch.action;
  const elem = substAction.elem;
  return (
    <>
      <strong>Inserted</strong>: <span>{`'${elem.toString()}' in '${change.patch.position}'`}</span>
    </>
  );
};

const movePatchDescription = (change: IClientPatch<IPatchMoveAction>) => {
  const pos = change.patch.position.split(".");
  const substAction = change.patch.action;
  return (
    <>
      <p>
        <strong>Moved {R.last(pos)} From</strong>: <span>{`${substAction.from}`}</span>
      </p>
      <p>
        <strong>To</strong>: <span>{`${substAction.to}`}</span>
      </p>
    </>
  );
};

interface IDescribeProps {
  course: IFullCourse;
  change: IClientPatch<IPatchAction>;
}

export const ChangeDescription: React.FC<IDescribeProps> = ({ course, change }) => {
  const {
    action: { type },
  } = change.patch;
  const pos = change.patch.position.split(".");
  switch (type) {
    case PatchType.add:
      return addPatchDescription(change as IClientPatch<IPatchAddAction<any>>);
    case PatchType.del:
      return delPatchDescription(change as IClientPatch<IPatchDeleteAction<any>>);
    case PatchType.subst:
      return substPatchDescription(course, change as IClientPatch<IPatchSubstAction<any>>);
    case PatchType.move:
      return movePatchDescription(change as IClientPatch<IPatchMoveAction>);
    case PatchType.ins:
      return insPatchDescription(change as IClientPatch<IPatchInsertAction<any>>);
  }

  return (
    <>
      <strong>Change</strong>: <span>{`${R.last(pos)}`}</span>
    </>
  );
};

export const LocationDescription: React.FC<IDescribeProps> = ({ course, change }) => {
  const { position } = change.patch;

  if (position === "") {
    return <>migrate course</>;
  }

  const pos = change.patch.position.split(".");
  const path = findComponentPath(course, position);
  const target = getPatchTarget(path, pos, course);
  const masterLocale = fullCourseViews.masterLocale(course) as PreferredLocale;
  const getLabel = () => {
    if (target === false) {
      return "Course";
    } else if (isUnit(target)) {
      return "Unit";
    } else if (isInstruction(target)) {
      return "Instruction within Unit";
    }
    return "Unknown";
  };
  const getTitle = () => {
    if (target === false) {
      return fullCourseViews.masterLocaleTitle(course) || DefaultNames.Course;
    } else if (isUnit(target)) {
      return courseComponentViews.localizedTitle(masterLocale)(target) || DefaultNames.Unit;
    } else if (isInstruction(target)) {
      const unitView = path[path.length - 2];
      const unit = unitView && (unitView(course) as ICourseUnit);
      return courseComponentViews.localizedTitle(masterLocale)(unit) || DefaultNames.Unit;
    }
    return <>Unknown</>;
  };

  if (R.isNil(target)) {
    return <>`Change at ${position}`</>;
  }

  return (
    <>
      <strong>{getLabel()}</strong>: <span>{getTitle()}</span>
    </>
  );
};
